import { io, Socket } from 'socket.io-client';
import { QueryClient } from '@tanstack/react-query';

class SocketService {
  private socket: Socket | null = null;
  private queryClient: QueryClient | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private wsUrl: string = '';

  /**
   * Initialize WebSocket connection
   */
  connect(token: string, queryClient: QueryClient) {
    if (this.socket) {
      if (this.socket.connected) {
        console.log('🔌 Socket already connected');
        return;
      }
      // Avoid creating multiple sockets in React 18 StrictMode double-effects
      console.log('🔌 Socket already exists (connecting/reconnecting)');
      return;
    }
    this.queryClient = queryClient;
    // Use REACT_APP_WS_URL if set, otherwise use REACT_APP_API_URL (without /api), or default to localhost:5000
    this.wsUrl = process.env.REACT_APP_WS_URL ||
      'http://localhost:5000';

    console.log('🔌 Connecting to WebSocket server:', this.wsUrl);

    this.socket = io(this.wsUrl, {
      auth: {
        token
      },
      // Start with polling for maximum compatibility; upgrade to websocket if possible
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: this.maxReconnectAttempts
    });

    this.setupEventListeners();
  }

  /**
   * Setup all event listeners
   */
  private setupEventListeners() {
    if (!this.socket) return;

    // Connection events
    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected');
      console.log('✅ Socket ID:', this.socket?.id);
      this.reconnectAttempts = 0;
    });

    this.socket.on('connected', (data) => {
      console.log('✅ WebSocket authenticated:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);

      if (reason === 'io server disconnect') {
        // Server disconnected, attempt to reconnect
        this.socket?.connect();
      }
    });

    this.socket.on('connect_error', (error: Error & { type?: string; description?: string }) => {
      console.error('❌ WebSocket connection error:', error.message);
      console.error('❌ Connection details:', {
        url: this.wsUrl,
        message: error.message,
        type: error.type || 'unknown',
        description: error.description || 'No description available'
      });
      this.reconnectAttempts++;

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('❌ Max reconnection attempts reached');
        console.error('⚠️  Make sure the backend server is running on port 5000');
        console.error('⚠️  Check that FRONTEND_URL is set correctly in backend .env file');
      }
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log(`✅ WebSocket reconnected after ${attemptNumber} attempts`);
      this.reconnectAttempts = 0;
    });

    // Dashboard updates
    this.socket.on('dashboard:update', () => {
      console.log('📊 Dashboard update received');
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });

    // Trade updates
    this.socket.on('trade:new', (data) => {
      console.log('📈 New trade received:', data.trade);
      this.queryClient?.invalidateQueries({ queryKey: ['trades'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });

    this.socket.on('trade:status', (data) => {
      console.log('📈 Trade status update:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['trades'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });

    // Signal/Post updates
    this.socket.on('signal:new', (data) => {
      console.log('📢 New signal/post received:', data.signal);
      // Invalidate and refetch signals immediately
      this.queryClient?.invalidateQueries({ queryKey: ['signals'] });
      // Force refetch after a short delay to ensure backend has processed
      setTimeout(() => {
        this.queryClient?.refetchQueries({ queryKey: ['signals'] });
      }, 100);
    });

    this.socket.on('signal:like', (data) => {
      console.log('❤️ Signal like update:', data);
      // Invalidate signals queries to refetch with updated likes array
      this.queryClient?.invalidateQueries({ queryKey: ['signals'] });
    });

    // Notification updates
    this.socket.on('notification:new', (data) => {
      console.log('🔔 New notification received:', data.notification);
      this.queryClient?.invalidateQueries({ queryKey: ['notifications'] });
      this.queryClient?.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });

      // Show browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(data.notification.title, {
          body: data.notification.message,
          icon: '/logo192.png',
          badge: '/logo192.png'
        });
      }
    });

    this.socket.on('notification:read', () => {
      console.log('✅ Notification marked as read');
      this.queryClient?.invalidateQueries({ queryKey: ['notifications'] });
      this.queryClient?.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    });

    this.socket.on('notification:unread-count', (data) => {
      console.log('🔔 Unread count update:', data.unreadCount);
      // Optimistically update unread count
      this.queryClient?.setQueryData(['notifications', 'unread-count'], {
        success: true,
        unreadCount: data.unreadCount
      });
    });

    // Balance/Position updates
    this.socket.on('balance:update', (data) => {
      console.log('💰 Balance update received:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
      this.queryClient?.invalidateQueries({ queryKey: ['brokerage', 'positions'] });
      this.queryClient?.invalidateQueries({ queryKey: ['brokerage', 'orders'] });
    });

    // Copy relationship updates
    this.socket.on('copy:update', (data) => {
      console.log('🔄 Copy relationship update:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['copyRelationships'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });
    
    // Broker-specific copy trade updates
    this.socket.on('copy:executed', (data) => {
      console.log('📋 Copy trade executed:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['trades'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
      this.queryClient?.invalidateQueries({ queryKey: ['brokerage', 'positions'] });
      this.queryClient?.invalidateQueries({ queryKey: ['brokerage', 'orders'] });
    });
    
    this.socket.on('copy:failed', (data) => {
      console.log('❌ Copy trade failed:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['trades'] });
    });
    
    // Brokerage connection updates
    this.socket.on('brokerage:update', (data) => {
      console.log('🔗 Brokerage connection update:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['brokerage', 'connections'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });
    
    // Master trade notifications (for followers)
    this.socket.on('master:trade', (data) => {
      console.log('👤 Master trade notification:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard', 'masters'] });
      this.queryClient?.invalidateQueries({ queryKey: ['masterDetails'] });
    });

    // Comment updates
    this.socket.on('comment:new', (data) => {
      console.log('💬 New comment received:', data.comment);
      this.queryClient?.invalidateQueries({ queryKey: ['comments', data.signalId] });
    });

    this.socket.on('comment:like', (data) => {
      console.log('❤️ Comment like update:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['comments', data.signalId] });
    });

    this.socket.on('comment:deleted', (data) => {
      console.log('🗑️ Comment deleted:', data);
      this.queryClient?.invalidateQueries({ queryKey: ['comments', data.signalId] });
    });

    // Subscription updates
    this.socket.on('subscription:update', (data) => {
      console.log('💳 Subscription update received:', data);
      // Update subscription cache immediately
      this.queryClient?.setQueryData(['subscription', 'status'], {
        success: true,
        hasSubscription: data.hasSubscription,
        isActive: data.isActive,
        subscription: data.subscription
      });
      // Also invalidate to refetch fresh data
      this.queryClient?.invalidateQueries({ queryKey: ['subscription'] });
      this.queryClient?.invalidateQueries({ queryKey: ['dashboard'] });
    });

    // Connection broken/fixed alerts
    this.socket.on('connection:broken', (data) => {
      console.log('⚠️ Connection broken alert received:', data);
      // Emit custom event for the alert component to listen to
      window.dispatchEvent(new CustomEvent('connection:broken', { detail: data }));
    });

    this.socket.on('connection:fixed', (data) => {
      console.log('✅ Connection fixed notification received:', data);
      // Emit custom event for the alert component to listen to
      window.dispatchEvent(new CustomEvent('connection:fixed', { detail: data }));
    });
  }

  /**
   * Disconnect WebSocket
   */
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket...');
      this.socket.disconnect();
      this.socket = null;
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  /**
   * Get socket instance
   */
  getSocket(): Socket | null {
    return this.socket;
  }

  /**
   * Subscribe to a socket event
   */
  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  /**
   * Unsubscribe from a socket event
   */
  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }

  /**
   * Emit an event to the server
   */
  emit(event: string, data?: any) {
    if (this.socket) {
      this.socket.emit(event, data);
    }
  }
}

// Export singleton instance
export const socketService = new SocketService();
